import { message, Modal } from 'antd'
import md5 from 'md5'
import React from 'react'
import {
  all,
  call,
  delay,
  fork,
  put,
  select,
  takeEvery,
  takeLeading,
} from 'redux-saga/effects'
import * as analyticsConstants from '../constants/analytics'
import * as folderConstants from '../constants/folder'
import SessionTimeoutError from '../modules/SessionTimeoutError'
import {
  assetName,
  buildPath,
  displayFolderName,
  recordAnalyticsEvent,
  validatePrivilege,
} from '../modules/utils'
import * as apiActions from './api'
import * as appActions from './appActions'
import * as assetActions from './asset'
import * as folderActions from './folder'
import * as modalActions from './modal'
import * as notificationActions from './notifications'
import * as settingsActions from './settings'
import * as userActions from './user'
import helpers from './utils/helpers'

export const PENDING_APPROVAL = 'PENDING_APPROVAL'
export const APPROVED = 'APPROVED'
export const REJECTED = 'REJECTED'
export const PENDING_REMOVAL = 'PENDING_REMOVAL'
export const UNPUBLISHED = 'UNPUBLISHED'
export const ASSET_STATUS_STATES = [
  UNPUBLISHED,
  PENDING_APPROVAL,
  APPROVED,
  REJECTED,
  PENDING_REMOVAL,
]

export const ADD_ENDPOINT = 'ADD_ENDPOINT'
export const ADD_ENDPOINT_SUCCESS = 'ADD_ENDPOINT_SUCCESS'
export const DELETE_ENDPOINT = 'DELETE_ENDPOINT'
export const DELETE_ENDPOINT_SUCCESS = 'DELETE_ENDPOINT_SUCCESS'
export const LOAD_ENDPOINT_STATE = 'LOAD_ENDPOINT_STATE'
export const PUBLISH_ASSET = 'PUBLISH_ASSET'
export const PUBLISH_ASSET_SUCCESS = 'PUBLISH_ASSET_SUCCESS'
export const PUBLISH_AND_APPROVE_ASSET = 'PUBLISH_AND_APPROVE_ASSET'
export const PUBLISH_AND_APPROVE_ASSET_SUCCESS =
  'PUBLISH_AND_APPROVE_ASSET_SUCCESS'
export const BATCH_PUBLISH = 'BATCH_PUBLISH'
export const PUBLISH_FOLDER = 'PUBLISH_FOLDER'
export const PUBLISH_AND_APPROVE_FOLDER = 'PUBLISH_AND_APPROVE_FOLDER'
export const UNPUBLISH_FOLDER = 'UNPUBLISH_FOLDER'
export const RENAME_ENDPOINT = 'RENAME_ENDPOINT'
export const RENAME_ENDPOINT_SUCCESS = 'RENAME_ENDPOINT_SUCCESS'
export const REVIEW_ASSET = 'REVIEW_ASSET'
export const REVIEW_ASSET_SUCCESS = 'REVIEW_ASSET_SUCCESS'
export const UNPUBLISH_ASSET = 'UNPUBLISH_ASSET'
export const UNPUBLISH_ASSET_SUCCESS = 'UNPUBLISH_ASSET_SUCCESS'
export const UNPUBLISH_AND_APPROVE_ASSET = 'UNPUBLISH_AND_APPROVE_ASSET'
export const UNPUBLISH_AND_APPROVE_ASSET_SUCCESS =
  'UNPUBLISH_AND_APPROVE_ASSET_SUCCESS'
export const REVIEW_UNPUBLISH_ASSET = 'REVIEW_UNPUBLISH_ASSET'
export const REVIEW_UNPUBLISH_ASSET_SUCCESS = 'REVIEW_UNPUBLISH_ASSET_SUCCESS'
export const UNPUBLISH = 'UNPUBLISH'
export const UNPUBLISH_STARTED = 'UNPUBLISH_STARTED'
export const UNPUBLISH_FINISHED = 'UNPUBLISH_FINISHED'

let unpublishSeq = 0

/*
 * trackUnpublish - wraps an unpublish saga so the asset sidebar can show which
 *  locations are being removed while it runs. The sagas take ~10s (a full
 *  state read, the action post and an SNS state update) and used to leave the
 *  Locations panel unchanged the whole time. `describe` maps the action to
 *  { folder, assets }; assets: null means everything under folder (a folder
 *  unpublish). A null description runs the saga untracked. The finally block
 *  clears the entry on success, failure and early return alike.
 */
const trackUnpublish = (saga, describe) =>
  function* trackedUnpublishSaga(action) {
    const entry = describe(action)
    if (!entry) return yield call(saga, action)

    const id = ++unpublishSeq
    yield put({ type: UNPUBLISH_STARTED, id, ...entry })
    try {
      return yield call(saga, action)
    } finally {
      yield put({ type: UNPUBLISH_FINISHED, id })
    }
  }

// Unpublish actions carry asset ids, though some comments call them objects
const unpublishTarget = ({ parent, assets }) => ({
  folder: parent,
  assets: (assets || []).map((a) => (a && typeof a === 'object' ? a.id : a)),
})

/**
 * Action creator for addEndpoint saga
 * @param {String} client client id
 * @param {String} name endpoint name
 * @param {Object} options endpoint options, such as description, badges, etc
 */
export const addEndpoint = (client, name, options) => ({
  type: ADD_ENDPOINT,
  client,
  name,
  options,
})

export function* watchAddEndpoint() {
  yield takeLeading(ADD_ENDPOINT, addEndpointSaga)
}

export function* addEndpointSaga(action) {
  yield put({ type: appActions.INITIATE_ASYNC })

  try {
    const config = yield select((state) => state.config.appConfig)
    const { endpoints } = yield select((state) => state.endpoints)
    const highestSortOrder = endpoints.reduce(
      (x, y) => Math.max(x.sortOrder || 0, y.sortOrder || 0),
      0,
    )
    const events = []

    action.endpoint = {
      actionIcon: '',
      allowPublishing: false,
      badges: [],
      description: '',
      client: action.client,
      icon: '',
      id: `WEB-${md5(`${action.name}${Date.now()}`)}`,
      name: action.name,
      privilege: '',
      rootFolders: [],
      views: [],
      sortOrder: highestSortOrder + 1,
      ...action.options,
    }

    yield call(apiActions.secureFetchSaga, {
      url: `${config.baseUrl}/api/state/addaction`,
      init: {
        method: 'POST',
        body: JSON.stringify({ action, events }),
      },
    })

    yield put({ type: ADD_ENDPOINT_SUCCESS, endpoint: action.endpoint })
  } catch (err) {
    console.error(err)
    if (err instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    }
  } finally {
    yield put({ type: appActions.COMPLETE_ASYNC })
  }
}

export const deleteEndpoint = (endpointId, confirm = true) => ({
  type: DELETE_ENDPOINT,
  endpointId,
  confirm,
})

export function* watchDeleteEndpoint() {
  yield takeLeading(DELETE_ENDPOINT, deleteEndpointSaga)
}

export function* deleteEndpointSaga(action) {
  if (action.confirm) {
    yield call(
      () =>
        new Promise((resolve, reject) => {
          Modal.confirm({
            cancelText: 'No',
            content:
              'Deleting an endpoint cannot be undone. Any assets published to it will be ' +
              'unpublished. Do you wish to delete this endpoint?',
            okText: 'Yes',
            okType: 'danger',
            onCancel: () => reject(),
            onOk: () => resolve(),
            title: 'Delete endpoint',
          })
        }),
    )
  }

  try {
    yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })

    const { endpointState } = yield call(appActions.refreshStateSaga)
    const endpoint = endpointState.endpoints.find(
      (e) => e.id === action.endpointId,
    )
    if (!endpoint) return

    /*
     * Deleting an endpoint takes its published assets with it: each root
     * folder goes through unpublishFolderSaga, which unpublishes every asset
     * in the tree (keeping carousel / custom-menu / builder-referenced assets
     * available via Resources > Assets) and then moves the folder to the
     * recycle bin. No "unpublish everything first" refusal.
     */
    for (const rootFolder of endpoint.rootFolders) {
      yield call(unpublishFolderSaga, {
        type: UNPUBLISH_FOLDER,
        folder: rootFolder,
      })
    }

    // unpublishFolderSaga reports its own failures; only mark the endpoint
    // deleted once every root folder really reached the recycle bin.
    yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })
    const { folderState } = yield call(appActions.refreshStateSaga)
    const stranded = endpoint.rootFolders.filter((id) => {
      const folder = folderState.byId[id]
      return (
        folder &&
        !folder.deleted &&
        buildPath(id, folderState.byId).indexOf(folderState.recycleBin) < 0
      )
    })
    if (stranded.length > 0) return

    const config = yield select((state) => state.config.appConfig)
    const events = []

    yield call(apiActions.secureFetchSaga, {
      url: `${config.baseUrl}/api/state/addaction`,
      init: {
        method: 'POST',
        body: JSON.stringify({ action, events }),
      },
    })

    yield put({
      type: DELETE_ENDPOINT_SUCCESS,
      endpointId: action.endpointId,
    })
  } catch (err) {
    console.error(err)
    if (err instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    }
  } finally {
    yield put({ type: appActions.COMPLETE_ASYNC })
  }
}

/*
 * Public facing action creator for publishing assets
 * @param 'type' - constant action type
 * @param ['ids'] - array of asset IDs that was published
 * @param 'parent' - string ID of the parent folder that assets were published to
 * @param notifyOptions - push notification options
 */
export const publishAsset = (assets, parent, notifyOptions) => ({
  type: PUBLISH_ASSET,
  assets,
  parent,
  notifyOptions,
})

export function* watchPublishAsset() {
  yield takeEvery(PUBLISH_ASSET, publishAssetSaga)
}

/*
 * Saga for managing the asset publishing actions
 *
 *  Assigns user details to the incoming action
 *  Creates events to be logged in the event log
 *  Calls the addaction api to update cloudstate and log events
 */
export function* publishAssetSaga(action) {
  const {
    assets, // list of asset objects
    parent, // folder id string,
    notifyOptions: { pushNotification = false, pushNotificationMessage },
    type,
  } = action

  // ignoreUI / data: set by a caller that composes this with other steps
  // (movePublishedAssetsSaga) and manages the spinner, messages and state
  // refresh itself. Neither is sent to the API.
  const ignoreUI = action.ignoreUI
  delete action.ignoreUI

  if (!ignoreUI) yield put({ type: appActions.INITIATE_ASYNC })

  try {
    const config = yield select((state) => state.config.appConfig)
    const data = yield action.data || call(appActions.refreshStateSaga)
    delete action.data

    /* Check that folder exists, is not in the recycleBin,
     *  and does not currently have a copy of this asset
     */
    if (helpers.validatePublishRequest(action, data)) {
      const timestamp = yield call(Date.now)
      action.timestamp = timestamp
      const user = yield select((state) => state.user)
      action.user = user.id
      action.client = user.client

      let events = []
      assets.forEach((asset) => {
        events.push({
          target_id: asset,
          timestamp,
          event_type: type,
          user: user.id,
          attributes: [
            {
              name: 'folder',
              old_value: null,
              new_value: parent,
            },
            {
              name: 'status',
              old_value: null,
              new_value: PENDING_APPROVAL,
            },
          ],
        })
      })
      yield call(apiActions.secureFetchSaga, {
        url: `${config.baseUrl}/api/state/addaction`,
        init: {
          method: 'POST',
          body: JSON.stringify({ action, events }),
        },
      })

      if (pushNotification) {
        const { assetState, folderState } = data

        yield call(apiActions.secureFetchSaga, {
          url: `${config.baseUrl}/api/notifications/push`,
          init: {
            method: 'POST',
            body: JSON.stringify({
              assets: assets.map((asset) => ({
                id: asset,
                name:
                  assetState.byId[asset].title ||
                  assetState.byId[asset].filename,
              })),
              destination: {
                id: parent,
                name: displayFolderName(folderState.byId[parent].name),
              },
              message: pushNotificationMessage,
              pendingReview: true,
              senderId: user.id,
            }),
          },
        })
      }

      if (!ignoreUI) yield put({ type: appActions.COMPLETE_ASYNC })
      if (!ignoreUI) yield put({ type: modalActions.CLOSE_MODAL })
      yield put({
        type: PUBLISH_ASSET_SUCCESS,
        assets,
        parent,
        user: user.id,
        timestamp,
      })
      if (!ignoreUI) {
        if (assets.length > 1) {
          yield call(
            message.success,
            `Your request to publish ${assets.length} assets was submitted for review`,
            4,
          )
        } else {
          const filename = yield select(
            (state) => state.assets.byId[assets[0]].filename,
          )
          yield call(
            message.success,
            `Your request to publish ${filename} was submitted for review`,
            4,
          )
        }
        yield put({ type: appActions.CLEAR_SELECTED_ASSETS })
      }
      for (let asset of assets) {
        yield fork(recordAnalyticsEvent, PUBLISH_ASSET, {
          asset: data.assetState.byId[asset],
        })
      }
      return yield true
    } else {
      if (!ignoreUI) {
        yield put({ type: appActions.COMPLETE_ASYNC })
        yield put({
          type: modalActions.SET_MESSAGE,
          msg: {
            type: 'ERROR',
            msg: 'Asset could not be published.  The application is refreshed...please try again',
          },
        })
        yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
        yield put({ type: appActions.CLEAR_SELECTED_ASSETS })
      }
      return yield false
    }
  } catch (error) {
    if (ignoreUI) throw error
    // This occurs when Session times out and user chooses to quit
    if (error instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    } else {
      yield put({ type: appActions.COMPLETE_ASYNC })
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: {
          type: 'ERROR',
          msg: error.clientMessage || 'Failed to publish asset',
        },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
      console.log(error)
    }
    return yield false
  }
}

export const publishAndApproveAsset = (assets, parent, notifyOptions) => ({
  type: PUBLISH_AND_APPROVE_ASSET,
  assets,
  parent,
  notifyOptions,
})

export function* watchPublishAndApproveAsset() {
  yield takeEvery(PUBLISH_AND_APPROVE_ASSET, publishAndApproveSaga)
}

export function* publishAndApproveSaga(action) {
  const {
    assets, // list of asset objects
    parent, // folder id string,
    comments,
    notifyOptions: { pushNotification = false, pushNotificationMessage },
    type,
  } = action

  const ignoreUI = action.ignoreUI
  delete action.ignoreUI

  if (!ignoreUI)
    yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })

  try {
    const config = yield action.config ||
      select((state) => state.config.appConfig)
    delete action.config
    const data = yield action.data || call(appActions.refreshStateSaga)
    delete action.data
    const user = yield action.user || select((state) => state.user)
    delete action.user
    const allAssets = yield select((state) => state.assets.byId)

    // Check that folder exists, is not in the recycleBin, and does not currently have a copy of
    // this asset
    if (helpers.validatePublishRequest(action, data)) {
      const timestamp = yield call(Date.now)
      action.timestamp = timestamp
      action.statusType = APPROVED
      action.user = user.id
      action.client = user.client

      const allFolders = yield select((state) => state.folders.byId)

      let events = []
      assets.forEach((asset) => {
        events.push(
          {
            target_id: asset,
            timestamp,
            event_type: type,
            user: user.id,
            attributes: [
              {
                name: 'folder',
                old_value: null,
                new_value: parent,
              },
              {
                name: 'status',
                old_value: null,
                new_value: PENDING_APPROVAL,
              },
            ],
          },
          {
            target_id: asset,
            timestamp: timestamp + 1,
            event_type: type,
            user: user.id,
            attributes: [
              {
                name: 'status',
                old_value: PENDING_APPROVAL,
                new_value: APPROVED,
              },
              {
                name: 'folder',
                old_value: parent,
                new_value: parent,
              },
              {
                name: 'reviewer',
                old_value: null,
                new_value: user.id,
              },
              {
                name: 'comments',
                old_value: null,
                new_value: comments,
              },
            ],
          },
        )
      })
      yield call(apiActions.secureFetchSaga, {
        url: `${config.baseUrl}/api/state/addaction`,
        init: {
          method: 'POST',
          body: JSON.stringify({ action, events }),
        },
      })

      // publish a message to update state
      if (!ignoreUI)
        yield call(apiActions.secureFetchSaga, {
          url: `${config.baseUrl}/api/state/sns/update`,
          init: {
            method: 'GET',
          },
        })

      if (pushNotification) {
        const { assetState, folderState } = data

        yield call(apiActions.secureFetchSaga, {
          url: `${config.baseUrl}/api/notifications/push`,
          init: {
            method: 'POST',
            body: JSON.stringify({
              assets: assets.map((asset) => ({
                id: asset,
                name:
                  assetState.byId[asset].title ||
                  assetState.byId[asset].filename,
              })),
              destination: {
                id: parent,
                name: displayFolderName(folderState.byId[parent].name),
              },
              message: pushNotificationMessage,
              pendingReview: false,
              senderId: user.id,
            }),
          },
        })
      }

      if (!ignoreUI) yield put({ type: appActions.COMPLETE_ASYNC })
      if (!ignoreUI) yield put({ type: modalActions.CLOSE_MODAL })
      yield put({
        type: PUBLISH_AND_APPROVE_ASSET_SUCCESS,
        assets,
        parent,
        user: user.id,
        timestamp,
        statusType: action.statusType,
      })

      if (assets.length > 1) {
        if (!ignoreUI)
          yield call(
            message.success,
            `${assets.length} assets are now live within ${allFolders[parent].name}`,
            4,
          )
      } else {
        if (!ignoreUI)
          yield call(
            message.success,
            <span>
              {assetName(allAssets[assets[0]])} is now live within{' '}
              {allFolders[parent].name}
            </span>,
            4,
          )
      }

      if (!ignoreUI) yield put({ type: appActions.CLEAR_SELECTED_ASSETS })
      for (let asset of action.assets) {
        yield fork(recordAnalyticsEvent, PUBLISH_ASSET, {
          asset: data.assetState.byId[asset],
        })
        yield fork(
          recordAnalyticsEvent,
          analyticsConstants.PUBLISH_ASSET_APPROVE,
          {
            asset: data.assetState.byId[asset],
          },
        )
      }
    } else {
      if (!ignoreUI) yield put({ type: appActions.COMPLETE_ASYNC })
      if (!ignoreUI)
        yield put({
          type: modalActions.SET_MESSAGE,
          msg: {
            type: 'ERROR',
            msg: 'Asset could not be published.  The application is refreshed...please try again',
          },
        })
      if (!ignoreUI)
        yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
      yield put({ type: appActions.CLEAR_SELECTED_ASSETS })

      return yield false
    }

    return yield true
  } catch (error) {
    // This occurs when Session times out and user chooses to quit
    if (error instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    } else {
      if (!ignoreUI) yield put({ type: appActions.COMPLETE_ASYNC })
      if (!ignoreUI)
        yield put({
          type: modalActions.SET_MESSAGE,
          msg: {
            type: 'ERROR',
            msg: error.clientMessage || 'Failed to publish asset',
          },
        })
      if (!ignoreUI)
        yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
      console.log(error)
    }

    return yield false
  }
}

export const batchPublish = (publishes) => ({
  type: BATCH_PUBLISH,
  publishes,
})

export function* watchBatchPublish() {
  yield takeEvery(BATCH_PUBLISH, batchPublishSaga)
}

export function* batchPublishSaga(action) {
  const config = yield select((state) => state.config.appConfig)
  const user = yield select((state) => state.user)
  const data = yield call(appActions.refreshStateSaga)

  const publishes = action.publishes

  for (let i = 0; i < publishes.length; ++i) {
    console.log(`publish: ${i + 1} of ${publishes.length}`, publishes[i])
    const publishAction = publishes[i]
    try {
      const { assetId, targetId } = publishAction
      const publishAndApproveAction = {
        type: PUBLISH_AND_APPROVE_ASSET,
        config,
        user,
        data,
        parent: targetId,
        assets: [assetId],
        notifyOptions: { pushNotification: false },
        ignoreUI: true,
      }
      const result = yield call(publishAndApproveSaga, publishAndApproveAction)
      if (!result) throw new Error('publish failure')
      console.log('publish success', { publishAction })

      if (i % 15 === 0) {
        yield call(apiActions.secureFetchSaga, {
          url: `${config.baseUrl}/api/state/sns/update`,
          init: {
            method: 'GET',
          },
        })
      }
    } catch (error) {
      console.log('publish failure', { publishAction, error: error.message })
    }
  }
}

/*
 * Public facing action creators for publishing a Library folder tree to an endpoint
 * @param 'folder' - string ID of the Library folder to publish
 * @param 'parent' - string ID of the destination endpoint folder
 */
export const publishFolder = (folder, parent) => ({
  type: PUBLISH_FOLDER,
  folder,
  parent,
})

export const publishAndApproveFolder = (folder, parent) => ({
  type: PUBLISH_AND_APPROVE_FOLDER,
  folder,
  parent,
})

export function* watchPublishFolder() {
  yield takeEvery(PUBLISH_FOLDER, publishFolderSaga)
}

export function* watchPublishAndApproveFolder() {
  yield takeEvery(PUBLISH_AND_APPROVE_FOLDER, publishFolderSaga)
}

/*
 * Saga for publishing a folder, its subfolders, and all assets they contain
 *
 *  Mirrors the Library folder structure beneath the selected destination folder,
 *  reusing destination folders that already exist by name and creating the rest
 *  with ADD_FOLDER cloud actions.  Each folder's assets are then published into
 *  its mirrored folder.  PUBLISH_FOLDER leaves the assets PENDING_APPROVAL;
 *  PUBLISH_AND_APPROVE_FOLDER approves them immediately.
 */
export function* publishFolderSaga(action) {
  const approve = action.type === PUBLISH_AND_APPROVE_FOLDER

  yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })

  try {
    const config = yield select((state) => state.config.appConfig)
    const user = yield select((state) => state.user)
    const data = yield call(appActions.refreshStateSaga)
    const { folderState, assetState, endpointState } = data

    const sourceFolder = folderState.byId[action.folder]
    const destinationFolder = folderState.byId[action.parent]

    // Check that both folders exist and the destination is not in the recycleBin
    if (
      !sourceFolder ||
      !destinationFolder ||
      buildPath(action.parent, folderState.byId).indexOf(
        folderState.recycleBin,
      ) > -1
    ) {
      yield put({ type: appActions.COMPLETE_ASYNC })
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: {
          type: 'ERROR',
          msg: 'Folder could not be published.  The application is refreshed...please try again',
        },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
      return
    }

    let addActionCount = 0

    /* Walk the source tree breadth-first, mirroring each folder beneath the
     * destination.  createdFolders/childAdditions track folders created during
     * this saga, since they are not yet part of the refreshed server state.
     */
    const createdFolders = {}
    const childAdditions = {}
    const folderPairs = []
    const queue = [{ sourceId: action.folder, targetParentId: action.parent }]

    while (queue.length) {
      const { sourceId, targetParentId } = queue.shift()
      const source = folderState.byId[sourceId]
      if (!source || source.deleted) continue

      // Reuse a destination folder with the same name if one already exists
      const targetParent =
        folderState.byId[targetParentId] || createdFolders[targetParentId]
      const targetChildren = (
        targetParent && targetParent.childFolders
          ? targetParent.childFolders
          : []
      ).concat(childAdditions[targetParentId] || [])
      let targetId = targetChildren.find((childId) => {
        const child = folderState.byId[childId] || createdFolders[childId]
        return child && !child.deleted && child.name === source.name
      })

      if (!targetId) {
        const timestamp = yield call(Date.now)
        targetId = md5(`${source.name}${user.id}${timestamp}`)
        const folder = {
          id: targetId,
          name: source.name,
          parentId: targetParentId,
          childFolders: [],
          assets: [],
          createdDt: timestamp,
          owner: user.id,
          client: user.client,
          deleted: false,
          type: folderConstants.DEFAULT_FOLDER_TYPE,
          contentRoles: folderActions.corePermissions,
        }

        yield call(apiActions.secureFetchSaga, {
          url: `${config.baseUrl}/api/state/addaction`,
          init: {
            method: 'POST',
            body: JSON.stringify({
              action: {
                type: folderActions.ADD_FOLDER,
                name: source.name,
                parent: { id: targetParentId },
                owner: user.id,
                user: user.id,
                client: user.client,
                permissions: folderActions.corePermissions,
                path: [],
                folderType: folderConstants.DEFAULT_FOLDER_TYPE,
                id: targetId,
                folder,
              },
              events: [],
            }),
          },
        })
        addActionCount++

        createdFolders[targetId] = folder
        childAdditions[targetParentId] = (
          childAdditions[targetParentId] || []
        ).concat([targetId])
        yield put({ type: folderActions.ADD_FOLDER_SUCCESS, folder })

        // every 15 actions, trigger a state snapshot
        if (addActionCount % 15 === 0) {
          yield call(apiActions.secureFetchSaga, {
            url: `${config.baseUrl}/api/state/sns/update`,
            init: { method: 'GET' },
          })
        }
      }

      folderPairs.push({ sourceId, targetId })
      const childFolders = source.childFolders || []
      childFolders.forEach((childId) =>
        queue.push({ sourceId: childId, targetParentId: targetId }),
      )
    }

    // Publish each folder's assets into its mirrored destination folder
    const publishType = approve ? PUBLISH_AND_APPROVE_ASSET : PUBLISH_ASSET
    let publishedAssetCount = 0

    for (let i = 0; i < folderPairs.length; ++i) {
      const { sourceId, targetId } = folderPairs[i]
      const statusForTarget = endpointState.folderAssetStatus[targetId] || {}
      const sourceAssets = folderState.byId[sourceId].assets || []
      const assets = sourceAssets.filter((assetId) => {
        const asset = assetState.byId[assetId]
        if (!asset || asset.deleted) return false
        // skip assets already published to (or pending in) the target folder
        const current = statusForTarget[assetId]
        return (
          !current ||
          current.status === UNPUBLISHED ||
          current.status === REJECTED
        )
      })
      if (assets.length === 0) continue

      const timestamp = yield call(Date.now)
      const publishAction = {
        type: publishType,
        assets,
        parent: targetId,
        timestamp,
        user: user.id,
        client: user.client,
      }
      if (approve) publishAction.statusType = APPROVED

      const events = []
      assets.forEach((asset) => {
        events.push({
          target_id: asset,
          timestamp,
          event_type: publishType,
          user: user.id,
          attributes: [
            {
              name: 'folder',
              old_value: null,
              new_value: targetId,
            },
            {
              name: 'status',
              old_value: null,
              new_value: PENDING_APPROVAL,
            },
          ],
        })
        if (approve) {
          events.push({
            target_id: asset,
            timestamp: timestamp + 1,
            event_type: publishType,
            user: user.id,
            attributes: [
              {
                name: 'status',
                old_value: PENDING_APPROVAL,
                new_value: APPROVED,
              },
              {
                name: 'folder',
                old_value: targetId,
                new_value: targetId,
              },
              {
                name: 'reviewer',
                old_value: null,
                new_value: user.id,
              },
              {
                name: 'comments',
                old_value: null,
                new_value: null,
              },
            ],
          })
        }
      })

      yield call(apiActions.secureFetchSaga, {
        url: `${config.baseUrl}/api/state/addaction`,
        init: {
          method: 'POST',
          body: JSON.stringify({ action: publishAction, events }),
        },
      })
      addActionCount++

      yield put({
        type: approve
          ? PUBLISH_AND_APPROVE_ASSET_SUCCESS
          : PUBLISH_ASSET_SUCCESS,
        assets,
        parent: targetId,
        user: user.id,
        timestamp,
        statusType: publishAction.statusType,
      })
      publishedAssetCount += assets.length

      for (let asset of assets) {
        yield fork(recordAnalyticsEvent, PUBLISH_ASSET, {
          asset: assetState.byId[asset],
        })
        if (approve) {
          yield fork(
            recordAnalyticsEvent,
            analyticsConstants.PUBLISH_ASSET_APPROVE,
            {
              asset: assetState.byId[asset],
            },
          )
        }
      }

      // every 15 actions, trigger a state snapshot
      if (addActionCount % 15 === 0) {
        yield call(apiActions.secureFetchSaga, {
          url: `${config.baseUrl}/api/state/sns/update`,
          init: { method: 'GET' },
        })
      }
    }

    // one final state snapshot so the new folders and publishes are applied
    yield call(apiActions.secureFetchSaga, {
      url: `${config.baseUrl}/api/state/sns/update`,
      init: { method: 'GET' },
    })

    yield put({ type: appActions.COMPLETE_ASYNC })
    yield put({ type: modalActions.CLOSE_MODAL })

    if (approve) {
      yield call(
        message.success,
        `${sourceFolder.name} and ${publishedAssetCount} assets are now live within ${destinationFolder.name}`,
        4,
      )
    } else {
      yield call(
        message.success,
        `Your request to publish ${publishedAssetCount} assets from ${sourceFolder.name} was submitted for review`,
        4,
      )
    }
  } catch (error) {
    // This occurs when Session times out and user chooses to quit
    if (error instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    } else {
      yield put({ type: appActions.COMPLETE_ASYNC })
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: {
          type: 'ERROR',
          msg: error.clientMessage || 'Failed to publish folder',
        },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
      console.log(error)
    }
  }
}

export const renameEndpoint = (newName, id, showSpinner = true) => ({
  type: RENAME_ENDPOINT,
  newName,
  id,
  showSpinner,
})

export function* watchRenameEndpoint() {
  yield takeLeading(RENAME_ENDPOINT, renameEndpointSaga)
}

export function* renameEndpointSaga(action) {
  try {
    const { endpoints } = yield select((state) => state.endpoints)
    const endpoint = endpoints.find((e) => e.id === action.id)

    if (!action.newName) {
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: { type: 'ERROR', msg: 'Empty endpoint name is not allowed' },
      })
      return yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
    }

    if (endpoint.name === action.newName) {
      return
    }

    if (action.showSpinner) {
      yield put({ type: appActions.INITIATE_ASYNC })
    }

    const config = yield select((state) => state.config.appConfig)
    const user = yield select((state) => state.user)

    action.user = user.id
    action.client = user.client

    let events = []
    const result = yield call(apiActions.secureFetchSaga, {
      url: `${config.baseUrl}/api/state/addaction`,
      init: {
        method: 'POST',
        body: JSON.stringify({ action, events }),
      },
    })

    if (result.statusCode && result.statusCode !== 200) {
      throw new Error(result.message)
    } else {
      yield put({
        type: RENAME_ENDPOINT_SUCCESS,
        id: action.id,
        newName: action.newName,
      })
    }
  } catch (err) {
    if (err instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    } else {
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: {
          type: 'ERROR',
          msg: 'Unable to rename endpoint. Please try your request again',
        },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
    }
  } finally {
    if (action.showSpinner) {
      yield put({ type: appActions.COMPLETE_ASYNC })
    }
  }
}

// okFunc and approveFunc is passed from the connected component because
// it already has dispatch bound.  Importing dispatch caused
// unit test issues.
export const unpublish = (assets, folder, okFunc, approveFunc) => ({
  type: UNPUBLISH,
  assets,
  folder,
  okFunc,
  approveFunc,
})

export function* watchUnpublish() {
  yield takeEvery(UNPUBLISH, unpublishSaga)
}

export function* unpublishSaga(action) {
  const { assets, okFunc, approveFunc } = action

  yield call(settingsActions.refreshSettingsSaga)

  // Single-action unpublish: users with approval rights unpublish
  // immediately; everyone else sends an approval request. The message
  // reflects which of the two will happen.
  const subject =
    assets.length > 1
      ? 'Unpublishing these assets will remove them'
      : 'Unpublishing this asset will remove it'
  const removalMessage = `${subject} from the endpoint.  Continue?`
  const requestMessage =
    `${subject} from the endpoint once a reviewer approves your request.  ` +
    'Send the unpublish request?'

  // Assets that are featured, in the custom menu, or used by another
  // published asset are not refused: the approve/review sagas keep them
  // published in Resources > Assets (retainInUseAssetsSaga).
  yield call(Modal.confirm, {
    title: 'Unpublish Asset',
    okText: 'UNPUBLISH',
    onOk: () => {
      if (approveFunc) approveFunc()
      else okFunc()
      Promise.resolve({})
    },
    content: approveFunc ? removalMessage : requestMessage,
  })
}

export const unpublishAsset = (assets, parent) => ({
  type: UNPUBLISH_ASSET,
  assets,
  parent,
})

export function* watchUnpublishAsset() {
  yield takeEvery(
    UNPUBLISH_ASSET,
    trackUnpublish(unpublishAssetSaga, unpublishTarget),
  )
}

export function* unpublishAssetSaga(action) {
  // ignoreUI / data: see publishAssetSaga
  const ignoreUI = action.ignoreUI
  delete action.ignoreUI

  if (!ignoreUI)
    yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })
  try {
    const config = yield select((state) => state.config.appConfig)
    const data = yield action.data || call(appActions.refreshStateSaga)
    delete action.data
    if (helpers.validateUnpublishRequest(action, data)) {
      const timestamp = yield call(Date.now)
      const user = yield select((state) => state.user)
      action.user = user.id
      action.client = user.client
      action.statusType = PENDING_REMOVAL
      action.timestamp = timestamp
      // Build events for event log
      let events = []
      for (let asset of action.assets) {
        events.push({
          target_id: asset,
          event_type: action.type,
          timestamp,
          user: action.user,
          attributes: [
            {
              name: 'status',
              old_value: APPROVED,
              new_value: PENDING_REMOVAL,
            },
            {
              name: 'folder',
              old_value: action.parent,
              new_value: action.parent,
            },
            {
              name: 'requester',
              old_value: null,
              new_value: action.user,
            },
          ],
        })
      }
      yield call(apiActions.secureFetchSaga, {
        url: `${config.baseUrl}/api/state/addaction`,
        init: {
          method: 'POST',
          body: JSON.stringify({ action, events }),
        },
      })

      if (!ignoreUI) yield put({ type: appActions.COMPLETE_ASYNC })
      if (!ignoreUI) yield put({ type: modalActions.CLOSE_MODAL })
      yield put({
        type: UNPUBLISH_ASSET_SUCCESS,
        assets: action.assets,
        parent: action.parent,
        statusType: PENDING_REMOVAL,
        user: action.user,
        client: action.client,
        timestamp,
      })
      if (!ignoreUI) {
        if (action.assets.length > 1) {
          yield call(
            message.success,
            `Your request to remove ${action.assets.length} assets was submitted.`,
            4,
          )
        } else {
          const assets = yield select((state) => state.assets.byId)
          yield call(
            message.success,
            `Your request to remove ${
              assets[action.assets[0]].title ||
              assets[action.assets[0]].filename
            } was submitted.`,
            4,
          )
        }
        yield put({ type: appActions.CLEAR_SELECTED_ASSETS })
      }
      for (let asset of action.assets) {
        yield fork(recordAnalyticsEvent, UNPUBLISH_ASSET, {
          asset: data.assetState.byId[asset],
        })
      }
      return yield true
    } else {
      if (!ignoreUI) {
        yield put({ type: appActions.COMPLETE_ASYNC })
        yield put({
          type: modalActions.SET_MESSAGE,
          msg: {
            type: 'ERROR',
            msg: 'Asset could not be unpublished.  The application is refreshed...please try again',
          },
        })
        yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
        yield put({ type: appActions.CLEAR_SELECTED_ASSETS })
      }
      return yield false
    }
  } catch (error) {
    if (ignoreUI) throw error
    // This occurs when Session times out and user chooses to quit
    if (error instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    } else {
      yield put({ type: appActions.COMPLETE_ASYNC })
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: {
          type: 'ERROR',
          msg: error.clientMessage || 'Failed to unpublish asset',
        },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
      console.log(error)
    }
    return yield false
  }
}

export const unpublishAndApprove = (assets, parent) => ({
  type: UNPUBLISH_AND_APPROVE_ASSET,
  assets,
  parent,
})

export function* watchUnpublishAndApprove() {
  yield takeEvery(
    UNPUBLISH_AND_APPROVE_ASSET,
    trackUnpublish(unpublishAndApproveSaga, unpublishTarget),
  )
}

export function* unpublishAndApproveSaga(action) {
  const {
    assets, // list of asset objects
    parent, // folder id string,
    type,
    comments,
  } = action

  // ignoreUI / data: see publishAssetSaga
  const ignoreUI = action.ignoreUI
  delete action.ignoreUI

  if (!ignoreUI)
    yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })

  try {
    const config = yield select((state) => state.config.appConfig)
    const data = yield action.data || call(appActions.refreshStateSaga)
    delete action.data
    const allAssets = yield select((state) => state.assets.byId)
    const allFolders = yield select((state) => state.folders.byId)

    if (helpers.validateUnpublishRequest(action, data)) {
      const timestamp = yield call(Date.now)
      const user = yield select((state) => state.user)
      action.user = user.id
      action.client = user.client
      action.timestamp = timestamp
      action.statusType = UNPUBLISHED

      yield call(retainInUseAssetsSaga, {
        config,
        user,
        data,
        assets,
        folders: [parent],
      })

      // ** CREATE events for the events table ** //
      let events = []
      for (let asset of assets) {
        events.push(
          {
            target_id: asset,
            event_type: type,
            timestamp,
            user: user.id,
            attributes: [
              {
                name: 'status',
                old_value: APPROVED,
                new_value: PENDING_REMOVAL,
              },
              {
                name: 'folder',
                old_value: parent,
                new_value: parent,
              },
              {
                name: 'requester',
                old_value: null,
                new_value: user.id,
              },
            ],
          },
          {
            target_id: asset,
            event_type: type,
            timestamp: timestamp + 1,
            user: user.id,
            attributes: [
              {
                name: 'status',
                old_value: PENDING_REMOVAL,
                new_value: action.statusType,
              },
              {
                name: 'folder',
                old_value: parent,
                new_value: parent,
              },
              {
                name: 'reviewer',
                old_value: null,
                new_value: user.id,
              },
              {
                name: 'comments',
                old_value: null,
                new_value: comments,
              },
            ],
          },
        )
      }
      // ** END CREATE  ** //

      // ** ADD Actions/Events to DAM ** //
      yield call(apiActions.secureFetchSaga, {
        url: `${config.baseUrl}/api/state/addaction`,
        init: {
          method: 'POST',
          body: JSON.stringify({ action, events }),
        },
      })
      // ** END ADD ** //

      // ** CALL to force update the DAM state ** //
      yield call(apiActions.secureFetchSaga, {
        url: `${config.baseUrl}/api/state/sns/update`,
        init: {
          method: 'GET',
        },
      })
      // ** END CALL ** //

      // ** UPDATE the local Redux store ** //
      yield put({
        type: UNPUBLISH_AND_APPROVE_ASSET_SUCCESS,
        assets: assets,
        parent: parent,
        comments: comments,
        statusType: action.statusType,
        user: user.id,
        timestamp,
      })
      // ** End UPDATE ** //

      if (!ignoreUI) {
        yield put({ type: appActions.COMPLETE_ASYNC })
        yield put({ type: appActions.CLEAR_SELECTED_ASSETS })
        yield put({ type: modalActions.CLOSE_MODAL })

        // ** DISPLAY UI notification messages ** //
        let msg
        if (assets.length > 1) {
          msg = `${assets.length} assets have been removed from ${allFolders[parent].name}`
          message.success(msg, 4)
        } else {
          msg = (
            <span>
              {assetName(allAssets[assets[0]])} has been removed from{' '}
              {allFolders[parent].name}
            </span>
          )
          message.success(msg, 4)
        }
        // ** END DISPLAY ** //
      }

      // ** LOG Analytics ** //
      for (let asset of assets) {
        yield fork(recordAnalyticsEvent, UNPUBLISH_ASSET, {
          asset: data.assetState.byId[asset],
        })
        yield fork(
          recordAnalyticsEvent,
          analyticsConstants.UNPUBLISH_ASSET_APPROVE,
          {
            asset: data.assetState.byId[asset],
          },
        )
      }
      // ** END LOG Analytics ** //
      return yield true
    } else {
      // validateUnpublishRequest has returned FALSEY
      if (!ignoreUI) {
        yield put({ type: appActions.COMPLETE_ASYNC })
        yield put({
          type: modalActions.SET_MESSAGE,
          msg: {
            type: 'ERROR',
            msg:
              'Asset could not be unpublished.  The application is refreshed...please check ' +
              'status and try again',
          },
        })
        yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
        yield put({ type: appActions.CLEAR_SELECTED_ASSETS })
      }
      return yield false
    }
  } catch (error) {
    if (ignoreUI) throw error
    console.log(error)
    // Session Timeout and user chooses to QUIT
    if (error instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    } else {
      // Something else has thrown inside the saga
      yield put({ type: appActions.COMPLETE_ASYNC })
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: {
          type: 'ERROR',
          msg: error.clientMessage || 'Asset review failed',
        },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
    }
    return yield false
  }
}

/*
 * movePublishedAssets - drag-and-drop move of published assets from one
 * folder of a publishing endpoint to another.
 *
 * A published asset is not "in" a folder the way a Library asset is: it is
 * published into it, with a per-folder status. So a move is two existing
 * operations run back to back - publish into the destination, then unpublish
 * from the source - each recorded through the normal addaction/event path.
 *
 * Users holding approveasset on both folders move the asset immediately (it
 * is APPROVED in the destination the moment it leaves the source). Anyone
 * else submits a pair of review requests: the asset is pending approval in
 * the destination and pending removal from the source until a reviewer acts.
 *
 * The publish runs first so the asset is never published nowhere in between;
 * retainInUseAssetsSaga (inside the unpublish step) is handed a state that
 * already records the destination so a featured or referenced asset is not
 * needlessly parked in Resources > Assets.
 */
export const MOVE_PUBLISHED_ASSETS = 'MOVE_PUBLISHED_ASSETS'

export const movePublishedAssets = (assets, sourceFolder, destFolder) => ({
  type: MOVE_PUBLISHED_ASSETS,
  assets,
  sourceFolder,
  destFolder,
})

export function* watchMovePublishedAssets() {
  yield takeLeading(MOVE_PUBLISHED_ASSETS, movePublishedAssetsSaga)
}

const confirmMove = ({ title, content, okText }) =>
  new Promise((resolve) => {
    Modal.confirm({
      title,
      content,
      okText,
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    })
  })

export function* movePublishedAssetsSaga(action) {
  const { assets, sourceFolder, destFolder } = action
  if (!assets || assets.length === 0 || sourceFolder === destFolder) return

  const user = yield select((state) => state.user)
  const { privileges, groups } = yield select((state) => state.permissions)
  const allFolders = yield select((state) => state.folders.byId)
  const allAssets = yield select((state) => state.assets.byId)
  const source = allFolders[sourceFolder]
  const dest = allFolders[destFolder]
  if (!source || !dest) return

  const can = (privilege, folder) =>
    validatePrivilege(
      privilege,
      privileges,
      user.email,
      groups,
      folder,
      allFolders,
    )
  const approve =
    can('approveasset', sourceFolder) && can('approveasset', destFolder)

  const what =
    assets.length > 1
      ? `${assets.length} assets`
      : assetName(allAssets[assets[0]]) || 'this asset'
  const confirmed = yield call(confirmMove, {
    title: approve ? 'Move Published Assets' : 'Request Move',
    okText: approve ? 'MOVE' : 'SEND REQUEST',
    content: approve
      ? `Move ${what} from ${source.name} to ${dest.name}? ` +
        `${assets.length > 1 ? 'They' : 'It'} will go live in ${dest.name} ` +
        `and be removed from ${source.name}.`
      : `${what} will be published to ${dest.name} and removed from ` +
        `${source.name} once a reviewer approves your requests. Send them?`,
  })
  if (!confirmed) return

  yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })
  try {
    const data = yield call(appActions.refreshStateSaga)

    // Step 1: publish into the destination
    const publishStep = {
      type: approve ? PUBLISH_AND_APPROVE_ASSET : PUBLISH_ASSET,
      assets,
      parent: destFolder,
      notifyOptions: {},
      ignoreUI: true,
      data,
    }
    const published = yield call(
      approve ? publishAndApproveSaga : publishAssetSaga,
      publishStep,
    )
    if (!published) {
      throw Object.assign(new Error('publish step rejected'), {
        clientMessage:
          `${what} could not be published to ${dest.name}. ` +
          'The application is refreshed...please check status and try again',
      })
    }

    // Step 2: unpublish from the source, seen against a state that already
    // records the destination publish
    const dataAfterPublish = Object.assign({}, data, {
      assetState: Object.assign({}, data.assetState, {
        byId: Object.assign({}, data.assetState.byId),
      }),
      endpointState: Object.assign({}, data.endpointState, {
        folderAssetStatus: Object.assign(
          {},
          data.endpointState.folderAssetStatus,
          {
            [destFolder]: Object.assign(
              {},
              data.endpointState.folderAssetStatus[destFolder],
            ),
          },
        ),
      }),
    })
    assets.forEach((id) => {
      const asset = dataAfterPublish.assetState.byId[id]
      if (asset) {
        dataAfterPublish.assetState.byId[id] = Object.assign({}, asset, {
          publishedTo: (asset.publishedTo || [])
            .filter((folder) => folder !== destFolder)
            .concat([destFolder]),
        })
      }
      dataAfterPublish.endpointState.folderAssetStatus[destFolder][id] = {
        status: approve ? APPROVED : PENDING_APPROVAL,
      }
    })
    const unpublishStep = {
      type: approve ? UNPUBLISH_AND_APPROVE_ASSET : UNPUBLISH_ASSET,
      assets,
      parent: sourceFolder,
      ignoreUI: true,
      data: dataAfterPublish,
    }
    const unpublished = yield call(
      approve ? unpublishAndApproveSaga : unpublishAssetSaga,
      unpublishStep,
    )
    if (!unpublished) {
      throw Object.assign(new Error('unpublish step rejected'), {
        clientMessage:
          `${what} ${approve ? 'is now live' : 'was requested'} in ${dest.name}, ` +
          `but could not be removed from ${source.name}. ` +
          'The application is refreshed...please check status and try again',
      })
    }

    // publish a message to update state (the approve path's unpublish step
    // already did; the request path never does)
    if (!approve) {
      const config = yield select((state) => state.config.appConfig)
      yield call(apiActions.secureFetchSaga, {
        url: `${config.baseUrl}/api/state/sns/update`,
        init: { method: 'GET' },
      })
    }

    yield put({ type: appActions.COMPLETE_ASYNC })
    yield put({ type: appActions.CLEAR_SELECTED_ASSETS })
    yield call(
      message.success,
      approve
        ? `${what} moved from ${source.name} to ${dest.name}`
        : `Your requests to move ${what} from ${source.name} to ${dest.name} were submitted for review`,
      4,
    )
  } catch (error) {
    if (error instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
      return
    }
    console.log(error)
    yield put({ type: appActions.COMPLETE_ASYNC })
    yield put({
      type: modalActions.SET_MESSAGE,
      msg: {
        type: 'ERROR',
        msg: error.clientMessage || 'Failed to move published asset',
      },
    })
    yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
  }
}

/*
 * Keeps in-use assets published when they are about to be unpublished.
 *
 *  Of `assets` (about to be unpublished from the `folders` given), an asset
 *  must remain available when it would otherwise end up published nowhere and
 *  it is in the Featured Assets carousel, is a Custom Menu Asset, or is
 *  referenced by a builder asset that stays published (published elsewhere,
 *  or retained by these same rules). Those assets are published (approved) to
 *  Resources > Assets — created from the Resources folder's permissions when
 *  the tenant has none — carousel entries pointing at an affected folder are
 *  re-pointed, and a modal lists what was kept. Returns the retained ids.
 *
 *  `data` is the refreshed state from refreshStateSaga; the caller posts the
 *  unpublish itself afterwards.
 */
export function* retainInUseAssetsSaga({
  config,
  user,
  data,
  assets,
  folders,
}) {
  const { folderState, assetState } = data
  const inFolders = {}
  folders.forEach((id) => (inFolders[id] = true))
  const unpublishing = {}
  assets.forEach((id) => (unpublishing[id] = true))

  yield call(settingsActions.refreshSettingsSaga)
  const carousel = yield select((state) => state.settings.featured.carousel)
  const customAssets = yield select((state) =>
    state.settings.menu.custom.filter(
      (resource) => resource.resourceType === 'asset',
    ),
  )
  const referencedBy =
    (assetState.relationships &&
      assetState.relationships.assets &&
      assetState.relationships.assets.referencedBy) ||
    {}

  // Folders where the asset stays published after this unpublish
  const publishedElsewhere = (assetId) => {
    const asset = assetState.byId[assetId]
    return ((asset && asset.publishedTo) || []).filter((folderId) => {
      const target = folderState.byId[folderId]
      if (!target || target.deleted) return false
      return !(inFolders[folderId] && unpublishing[assetId])
    })
  }

  // reasons[assetId] = list of strings explaining why it is kept
  const reasons = {}
  const mustRetain = (assetId, visited) => {
    if (reasons[assetId] !== undefined) return reasons[assetId].length > 0
    if (visited.indexOf(assetId) > -1) return false
    if (publishedElsewhere(assetId).length > 0) {
      reasons[assetId] = []
      return false
    }
    const path = visited.concat(assetId)
    const why = []
    if (carousel.some((fa) => fa.asset === assetId))
      why.push('Featured Assets carousel')
    if (customAssets.some((val) => val.resourceValue === assetId))
      why.push('Custom Menu')
    ;(referencedBy[assetId] || []).forEach((ref) => {
      const referrer = assetState.byId[ref]
      if (!referrer || referrer.deleted) return
      if (publishedElsewhere(ref).length > 0 || mustRetain(ref, path))
        why.push(`Used by ${assetName(referrer)}`)
    })
    reasons[assetId] = why
    return why.length > 0
  }

  const retained = assets.filter(
    (assetId) => assetState.byId[assetId] && mustRetain(assetId, []),
  )
  if (retained.length === 0) return retained

  const resourcesId = folderConstants.ASSETS_RESOURCE_FOLDER
  let resourcesFolder = folderState.byId[resourcesId]

  // Create Resources > Assets when the tenant has none, inheriting the
  // Resources folder's permissions
  if (!resourcesFolder || resourcesFolder.deleted) {
    const resourcesRoot = folderState.byId[folderConstants.RESOURCES_FOLDER]
    if (!resourcesRoot || resourcesRoot.deleted) {
      const error = new Error('Resources folder missing')
      error.clientMessage =
        'An asset being unpublished is featured, in the custom menu, or used by another ' +
        'published asset, and there is no Resources folder to keep it published in.  ' +
        'Remove those references and try again.'
      throw error
    }
    const timestamp = yield call(Date.now)
    const contentRoles = JSON.parse(
      JSON.stringify(resourcesRoot.contentRoles || {}),
    )
    resourcesFolder = {
      id: resourcesId,
      name: 'Assets',
      description: 'Assets',
      parentId: folderConstants.RESOURCES_FOLDER,
      childFolders: [],
      assets: [],
      createdDt: timestamp,
      updatedDt: timestamp,
      owner: 'system',
      client: user.client,
      deleted: false,
      type: folderConstants.RESOURCES_FOLDER_TYPE,
      contentRoles,
    }
    yield call(apiActions.secureFetchSaga, {
      url: `${config.baseUrl}/api/state/addaction`,
      init: {
        method: 'POST',
        body: JSON.stringify({
          action: {
            type: folderActions.ADD_FOLDER,
            name: 'Assets',
            parent: { id: folderConstants.RESOURCES_FOLDER },
            owner: 'system',
            user: user.id,
            client: user.client,
            permissions: contentRoles,
            path: [],
            folderType: folderConstants.RESOURCES_FOLDER_TYPE,
            id: resourcesId,
            folder: resourcesFolder,
          },
          events: [],
        }),
      },
    })
    // ADD_FOLDER_SUCCESS makes the new folder active; keep the user's folder
    const activeFolder = yield select((state) => state.folders.activeFolder)
    yield put({
      type: folderActions.ADD_FOLDER_SUCCESS,
      folder: resourcesFolder,
    })
    if (activeFolder) yield put(folderActions.activeFolder(activeFolder))
  }

  // Publish the retained assets to Resources > Assets (approved) unless
  // they are already listed there
  const toResources = retained.filter(
    (assetId) => (resourcesFolder.assets || []).indexOf(assetId) < 0,
  )
  if (toResources.length > 0) {
    const timestamp = yield call(Date.now)
    const publishAction = {
      type: PUBLISH_AND_APPROVE_ASSET,
      assets: toResources,
      parent: resourcesId,
      timestamp,
      user: user.id,
      client: user.client,
      statusType: APPROVED,
    }
    const events = []
    toResources.forEach((asset) => {
      events.push(
        {
          target_id: asset,
          timestamp,
          event_type: PUBLISH_AND_APPROVE_ASSET,
          user: user.id,
          attributes: [
            { name: 'folder', old_value: null, new_value: resourcesId },
            { name: 'status', old_value: null, new_value: PENDING_APPROVAL },
          ],
        },
        {
          target_id: asset,
          timestamp: timestamp + 1,
          event_type: PUBLISH_AND_APPROVE_ASSET,
          user: user.id,
          attributes: [
            {
              name: 'status',
              old_value: PENDING_APPROVAL,
              new_value: APPROVED,
            },
            { name: 'folder', old_value: resourcesId, new_value: resourcesId },
            { name: 'reviewer', old_value: null, new_value: user.id },
            { name: 'comments', old_value: null, new_value: null },
          ],
        },
      )
    })
    yield call(apiActions.secureFetchSaga, {
      url: `${config.baseUrl}/api/state/addaction`,
      init: {
        method: 'POST',
        body: JSON.stringify({ action: publishAction, events }),
      },
    })
    yield put({
      type: PUBLISH_AND_APPROVE_ASSET_SUCCESS,
      assets: toResources,
      parent: resourcesId,
      user: user.id,
      timestamp,
      statusType: APPROVED,
    })
    for (let asset of toResources) {
      yield fork(recordAnalyticsEvent, PUBLISH_ASSET, {
        asset: assetState.byId[asset],
      })
      yield fork(
        recordAnalyticsEvent,
        analyticsConstants.PUBLISH_ASSET_APPROVE,
        {
          asset: assetState.byId[asset],
        },
      )
    }
  }

  // Re-point carousel entries for the unpublished assets at a folder where
  // the asset stays published
  let carouselChanged = false
  const newCarousel = carousel.map((entry) => {
    if (!inFolders[entry.folder] || !unpublishing[entry.asset]) return entry
    const folder =
      retained.indexOf(entry.asset) > -1
        ? resourcesId
        : publishedElsewhere(entry.asset)[0]
    if (!folder || folder === entry.folder) return entry
    carouselChanged = true
    return Object.assign({}, entry, { folder })
  })
  if (carouselChanged) {
    yield call(apiActions.secureFetchSaga, {
      url: `${config.baseUrl}/api/settings/update`,
      init: {
        method: 'POST',
        body: JSON.stringify({ featured: { carousel: newCarousel } }),
      },
    })
    yield put({
      type: settingsActions.SAVE_SETTINGS_SUCCESS,
      setting: 'featured',
      value: { carousel: newCarousel },
    })
  }

  yield call(Modal.info, {
    title: 'Assets kept published',
    width: 600,
    content: (
      <div>
        <p>
          {retained.length > 1
            ? 'These assets are still in use, so they have been kept published in '
            : 'This asset is still in use, so it has been kept published in '}
          Resources &gt; Assets and removed from the selected location only.
        </p>
        <ul>
          {retained.map((assetId) => (
            <li key={assetId}>
              <strong>{assetName(assetState.byId[assetId])}</strong>
              {' — '}
              {reasons[assetId].join(', ')}
            </li>
          ))}
        </ul>
      </div>
    ),
  })

  return retained
}

/*
 * Public facing action creator for unpublishing a published-endpoint folder
 * tree: every asset in the folder and its subfolders is unpublished, then the
 * folder (and with it the subfolders) is moved to the recycle bin.
 * @param 'folder' - string ID of the endpoint folder
 */
export const unpublishFolder = (folder) => ({
  type: UNPUBLISH_FOLDER,
  folder,
})

export function* watchUnpublishFolder() {
  yield takeLeading(
    UNPUBLISH_FOLDER,
    trackUnpublish(unpublishFolderSaga, ({ folder }) => ({
      folder,
      assets: null,
    })),
  )
}

/*
 * Saga for unpublishing a folder, its subfolders, and all assets they contain
 *
 *  The counterpart of publishFolderSaga. Walks the endpoint folder tree and,
 *  per folder, removes each asset according to its current status there:
 *    APPROVED         -> UNPUBLISH_AND_APPROVE_ASSET (removed immediately)
 *    PENDING_APPROVAL -> REVIEW_ASSET with REJECTED
 *    PENDING_REMOVAL  -> REVIEW_UNPUBLISH_ASSET with UNPUBLISHED
 *  Assets that must stay published — featured in the carousel, in the custom
 *  menu, or referenced by a builder that stays published — are first published
 *  to Resources > Assets instead of blocking the unpublish.
 *  Actions are posted directly (as publishFolderSaga does) rather than through
 *  the single-asset sagas, which each refresh the whole state and raise their
 *  own toasts. Once the tree holds no published assets the folder is moved to
 *  the recycle bin through dropFolderSaga, which deletes the subfolders with it.
 */
export function* unpublishFolderSaga(action) {
  yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })

  try {
    const config = yield select((state) => state.config.appConfig)
    const user = yield select((state) => state.user)
    const data = yield call(appActions.refreshStateSaga)
    const { folderState, assetState, endpointState } = data
    const folder = folderState.byId[action.folder]

    if (
      !folder ||
      folder.deleted ||
      buildPath(action.folder, folderState.byId).indexOf(
        folderState.recycleBin,
      ) > -1
    ) {
      yield put({ type: appActions.COMPLETE_ASYNC })
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: {
          type: 'ERROR',
          msg: 'Folder could not be unpublished.  The application is refreshed...please try again',
        },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
      return
    }

    // Collect the folder tree breadth-first
    const folderIds = []
    const queue = [action.folder]
    while (queue.length) {
      const id = queue.shift()
      const current = folderState.byId[id]
      if (!current || current.deleted || folderIds.indexOf(id) > -1) continue
      folderIds.push(id)
      ;(current.childFolders || []).forEach((childId) => queue.push(childId))
    }

    // Keep in-use assets published (Resources > Assets) before unpublishing
    const treeAssets = []
    folderIds.forEach((folderId) => {
      ;(folderState.byId[folderId].assets || []).forEach((assetId) => {
        if (assetState.byId[assetId] && treeAssets.indexOf(assetId) < 0)
          treeAssets.push(assetId)
      })
    })
    yield call(retainInUseAssetsSaga, {
      config,
      user,
      data,
      assets: treeAssets,
      folders: folderIds,
    })

    let actionCount = 0
    let unpublishedCount = 0

    for (let f = 0; f < folderIds.length; ++f) {
      const parent = folderIds[f]
      const statusForFolder = endpointState.folderAssetStatus[parent] || {}
      const folderAssets = (folderState.byId[parent].assets || []).filter(
        (assetId) => assetState.byId[assetId],
      )

      const byStatus = { approved: [], pending: [], removing: [] }
      folderAssets.forEach((assetId) => {
        const status =
          statusForFolder[assetId] && statusForFolder[assetId].status
        if (status === APPROVED) byStatus.approved.push(assetId)
        else if (status === PENDING_APPROVAL) byStatus.pending.push(assetId)
        else if (status === PENDING_REMOVAL) byStatus.removing.push(assetId)
      })

      const groups = [
        {
          assets: byStatus.approved,
          type: UNPUBLISH_AND_APPROVE_ASSET,
          successType: UNPUBLISH_AND_APPROVE_ASSET_SUCCESS,
          statusType: UNPUBLISHED,
          events: (asset, timestamp) => [
            {
              target_id: asset,
              event_type: UNPUBLISH_AND_APPROVE_ASSET,
              timestamp,
              user: user.id,
              attributes: [
                {
                  name: 'status',
                  old_value: APPROVED,
                  new_value: PENDING_REMOVAL,
                },
                { name: 'folder', old_value: parent, new_value: parent },
                { name: 'requester', old_value: null, new_value: user.id },
              ],
            },
            {
              target_id: asset,
              event_type: UNPUBLISH_AND_APPROVE_ASSET,
              timestamp: timestamp + 1,
              user: user.id,
              attributes: [
                {
                  name: 'status',
                  old_value: PENDING_REMOVAL,
                  new_value: UNPUBLISHED,
                },
                { name: 'folder', old_value: parent, new_value: parent },
                { name: 'reviewer', old_value: null, new_value: user.id },
                { name: 'comments', old_value: null, new_value: null },
              ],
            },
          ],
        },
        {
          assets: byStatus.pending,
          type: REVIEW_ASSET,
          successType: REVIEW_ASSET_SUCCESS,
          statusType: REJECTED,
          events: (asset, timestamp) => [
            {
              target_id: asset,
              event_type: REVIEW_ASSET,
              timestamp,
              user: user.id,
              attributes: [
                {
                  name: 'status',
                  old_value: PENDING_APPROVAL,
                  new_value: REJECTED,
                },
                { name: 'folder', old_value: parent, new_value: parent },
                { name: 'reviewer', old_value: null, new_value: user.id },
                { name: 'comments', old_value: null, new_value: null },
              ],
            },
          ],
        },
        {
          assets: byStatus.removing,
          type: REVIEW_UNPUBLISH_ASSET,
          successType: REVIEW_UNPUBLISH_ASSET_SUCCESS,
          statusType: UNPUBLISHED,
          events: (asset, timestamp) => [
            {
              target_id: asset,
              event_type: REVIEW_UNPUBLISH_ASSET,
              timestamp,
              user: user.id,
              attributes: [
                { name: 'status', old_value: APPROVED, new_value: UNPUBLISHED },
                { name: 'folder', old_value: parent, new_value: parent },
                { name: 'reviewer', old_value: null, new_value: user.id },
                { name: 'comments', old_value: null, new_value: null },
              ],
            },
          ],
        },
      ]

      for (let g = 0; g < groups.length; ++g) {
        const group = groups[g]
        if (group.assets.length === 0) continue

        const timestamp = yield call(Date.now)
        const unpublishAction = {
          type: group.type,
          assets: group.assets,
          parent,
          comments: null,
          statusType: group.statusType,
          user: user.id,
          client: user.client,
          timestamp,
        }
        const events = []
        group.assets.forEach((asset) =>
          events.push(...group.events(asset, timestamp)),
        )

        yield call(apiActions.secureFetchSaga, {
          url: `${config.baseUrl}/api/state/addaction`,
          init: {
            method: 'POST',
            body: JSON.stringify({ action: unpublishAction, events }),
          },
        })
        actionCount++
        unpublishedCount += group.assets.length

        yield put({
          type: group.successType,
          assets: group.assets,
          parent,
          comments: null,
          statusType: group.statusType,
          user: user.id,
          timestamp,
        })

        for (let asset of group.assets) {
          yield fork(recordAnalyticsEvent, UNPUBLISH_ASSET, {
            asset: assetState.byId[asset],
          })
          yield fork(
            recordAnalyticsEvent,
            analyticsConstants.UNPUBLISH_ASSET_APPROVE,
            { asset: assetState.byId[asset] },
          )
        }

        // every 15 actions, trigger a state snapshot
        if (actionCount % 15 === 0) {
          yield call(apiActions.secureFetchSaga, {
            url: `${config.baseUrl}/api/state/sns/update`,
            init: { method: 'GET' },
          })
        }
      }
    }

    // Snapshot so the state refresh inside dropFolderSaga sees the tree empty
    yield call(apiActions.secureFetchSaga, {
      url: `${config.baseUrl}/api/state/sns/update`,
      init: { method: 'GET' },
    })

    /*
     * The state API reads not-yet-snapshotted actions through a DynamoDB
     * global secondary index, which is eventually consistent, so a refresh
     * taken straight after the unpublish posts can still show the assets in
     * place -- and dropFolderSaga would then refuse the delete as "contains
     * a published or pending asset". Wait, re-reading, until the tree reads
     * empty (bounded; dropFolderSaga still makes the final call).
     */
    for (let attempt = 0; attempt < 8; ++attempt) {
      const refreshed = yield call(appActions.refreshStateSaga)
      const stillListed = folderIds.some((id) => {
        const current = refreshed.folderState.byId[id]
        return current && !current.deleted && (current.assets || []).length > 0
      })
      if (!stillListed) break
      yield delay(1500)
    }

    if (unpublishedCount > 0) {
      message.success(
        `${unpublishedCount} asset${unpublishedCount > 1 ? 's' : ''} unpublished from ${folder.name}`,
        4,
      )
    }

    // dropFolderSaga manages the spinner and modal state from here on
    yield call(folderActions.dropFolderSaga, {
      type: folderActions.MOVE_FOLDER,
      dropId: folderState.recycleBin,
      dragId: action.folder,
      parent: folder.parentId,
      dragData: { over: null, above: null, below: null },
    })
  } catch (error) {
    console.log(error)
    yield put({ type: appActions.COMPLETE_ASYNC })
    if (error instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    } else {
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: {
          type: 'ERROR',
          msg: error.clientMessage || 'Folder could not be unpublished',
        },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
    }
  }
}

export const UNPUBLISH_AND_DELETE_ASSET = 'UNPUBLISH_AND_DELETE_ASSET'

export const unpublishAndDelete = (assetId, sourceFolder, recycleBin) => ({
  type: UNPUBLISH_AND_DELETE_ASSET,
  assetId,
  sourceFolder,
  recycleBin,
})

export function* watchUnpublishAndDelete() {
  yield takeEvery(UNPUBLISH_AND_DELETE_ASSET, unpublishAndDeleteSaga)
}

export function* unpublishAndDeleteSaga(action) {
  yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })
  try {
    const config = yield select((state) => state.config.appConfig)
    const user = yield select((state) => state.user)
    const allAssets = yield select((state) => state.assets.byId)
    const allFolders = yield select((state) => state.folders.byId)
    const asset = allAssets[action.assetId]
    const publishedFolders = (asset && asset.publishedTo) || []
    const timestamp = yield call(Date.now)

    for (let i = 0; i < publishedFolders.length; i++) {
      const parent = publishedFolders[i]
      {
        const unpublishAction = {
          type: UNPUBLISH_AND_APPROVE_ASSET,
          assets: [action.assetId],
          parent,
          user: user.id,
          client: user.client,
          timestamp: timestamp + i * 2,
          statusType: UNPUBLISHED,
        }
        const events = [
          {
            target_id: action.assetId,
            event_type: UNPUBLISH_AND_APPROVE_ASSET,
            timestamp: timestamp + i * 2,
            user: user.id,
            attributes: [
              {
                name: 'status',
                old_value: APPROVED,
                new_value: PENDING_REMOVAL,
              },
              { name: 'folder', old_value: parent, new_value: parent },
              { name: 'requester', old_value: null, new_value: user.id },
            ],
          },
          {
            target_id: action.assetId,
            event_type: UNPUBLISH_AND_APPROVE_ASSET,
            timestamp: timestamp + i * 2 + 1,
            user: user.id,
            attributes: [
              {
                name: 'status',
                old_value: PENDING_REMOVAL,
                new_value: UNPUBLISHED,
              },
              { name: 'folder', old_value: parent, new_value: parent },
              { name: 'reviewer', old_value: null, new_value: user.id },
              { name: 'comments', old_value: null, new_value: null },
            ],
          },
        ]
        yield call(apiActions.secureFetchSaga, {
          url: `${config.baseUrl}/api/state/addaction`,
          init: {
            method: 'POST',
            body: JSON.stringify({ action: unpublishAction, events }),
          },
        })
      }
    }

    const deleteTimestamp = timestamp + publishedFolders.length * 2
    const deleteAction = {
      type: assetActions.DROP_ASSET,
      dropId: action.recycleBin,
      dragId: [action.assetId],
      sourceFolder: action.sourceFolder,
      user: user.id,
      client: user.client,
      timestamp: deleteTimestamp,
    }
    const deleteEvents = [
      {
        event_type: assetActions.DROP_ASSET,
        target_id: action.assetId,
        user: user.id,
        timestamp: deleteTimestamp,
        attributes: {
          name: 'folder',
          old_value: action.sourceFolder,
          new_value: action.recycleBin,
        },
      },
    ]
    yield call(apiActions.secureFetchSaga, {
      url: `${config.baseUrl}/api/state/addaction`,
      init: {
        method: 'POST',
        body: JSON.stringify({ action: deleteAction, events: deleteEvents }),
      },
    })

    yield call(apiActions.secureFetchSaga, {
      url: `${config.baseUrl}/api/state/sns/update`,
      init: { method: 'GET' },
    })

    yield put({
      type: assetActions.DROP_ASSET_SUCCESS,
      dropId: action.recycleBin,
      dragId: [action.assetId],
      sourceFolder: action.sourceFolder,
    })
  } catch (error) {
    if (error instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    } else {
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: { type: 'ERROR', msg: error.message || 'Unable to delete asset' },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
    }
  } finally {
    yield put({ type: appActions.CLEAR_SELECTED_ASSETS })
    yield put({ type: appActions.COMPLETE_ASYNC })
  }
}

export function* watchPublishAssetSuccess() {
  yield takeEvery(
    [PUBLISH_ASSET_SUCCESS, UNPUBLISH_ASSET_SUCCESS],
    notificationActions.sendPublishNotificationSaga,
  )
}

export const reviewAsset = (assets, parent, comments, statusType) => ({
  type: REVIEW_ASSET,
  assets,
  parent,
  comments,
  statusType,
})

export function* watchReviewAsset() {
  yield takeEvery(REVIEW_ASSET, reviewAssetSaga)
}

export function* reviewAssetSaga(action) {
  const {
    assets, // list of asset objects
    parent, // folder id string,
    statusType,
    type,
    comments,
  } = action

  yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })

  try {
    const config = yield select((state) => state.config.appConfig)
    const allAssets = yield select((state) => state.assets.byId)
    const data = yield call(appActions.refreshStateSaga)

    if (helpers.validateReviewPublishRequest(action, data)) {
      const timestamp = yield call(Date.now)
      const user = yield select((state) => state.user)
      action.user = user.id
      action.client = user.client
      action.timestamp = timestamp
      const allFolders = yield select((state) => state.folders.byId)

      let events = []
      for (let asset of assets) {
        events.push({
          target_id: asset,
          event_type: type,
          timestamp,
          user: user.id,
          attributes: [
            {
              name: 'status',
              old_value: PENDING_APPROVAL,
              new_value: statusType,
            },
            {
              name: 'folder',
              old_value: parent,
              new_value: parent,
            },
            {
              name: 'reviewer',
              old_value: null,
              new_value: user.id,
            },
            {
              name: 'comments',
              old_value: null,
              new_value: comments,
            },
          ],
        })
      }
      yield call(apiActions.secureFetchSaga, {
        url: `${config.baseUrl}/api/state/addaction`,
        init: {
          method: 'POST',
          body: JSON.stringify({ action, events }),
        },
      })

      // publish a message to update state
      yield call(apiActions.secureFetchSaga, {
        url: `${config.baseUrl}/api/state/sns/update`,
        init: {
          method: 'GET',
        },
      })

      yield call(apiActions.secureFetchSaga, {
        url: `${config.baseUrl}/api/notifications/pushPending`,
        init: {
          method: 'POST',
          body: JSON.stringify({ assets, destination: { id: parent } }),
        },
      })

      yield put({
        type: REVIEW_ASSET_SUCCESS,
        assets: assets,
        parent: parent,
        comments: comments,
        statusType: statusType,
        user: user.id,
        timestamp,
      })
      yield put({ type: appActions.CLEAR_SELECTED_ASSETS })
      yield put({ type: appActions.COMPLETE_ASYNC })

      let msg
      if (assets.length > 1) {
        if (statusType === APPROVED) {
          msg = `${assets.length} assets are now live within ${allFolders[parent].name}`
        } else {
          msg =
            `${assets.length} assets were rejected from being published within ` +
            `${allFolders[parent].name}`
        }
        yield call(message.success, msg, 4)
      } else {
        if (statusType === APPROVED) {
          msg = (
            <span>
              {assetName(allAssets[assets[0]])} is now live within{' '}
              {allFolders[parent].name}
            </span>
          )
        } else {
          msg = (
            <span>
              You rejected {assetName(allAssets[assets[0]])} from{' '}
              {allFolders[parent].name}
            </span>
          )
        }
        yield call(message.success, msg, 4)
      }
      for (let asset of assets) {
        yield fork(
          recordAnalyticsEvent,
          statusType === APPROVED
            ? analyticsConstants.PUBLISH_ASSET_APPROVE
            : analyticsConstants.PUBLISH_ASSET_REJECT,
          { asset: data.assetState.byId[asset] },
        )
      }
    } else {
      yield put({ type: appActions.COMPLETE_ASYNC })
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: {
          type: 'ERROR',
          msg:
            'Asset is not PENDING_APPROVAL.  The application is refreshed...please check ' +
            'status and try again',
        },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
      yield put({ type: appActions.CLEAR_SELECTED_ASSETS })
    }
  } catch (error) {
    console.log(error)
    // This occurs when Session times out and user chooses to quit
    if (error instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    } else {
      yield put({ type: appActions.COMPLETE_ASYNC })
    }
  }
}

export const reviewUnpublishAsset = (assets, parent, comments, statusType) => ({
  type: REVIEW_UNPUBLISH_ASSET,
  assets,
  parent,
  comments,
  statusType,
})

export function* watchReviewUnpublishAsset() {
  // Only an approval removes the asset; a rejected request leaves it published
  yield takeEvery(
    REVIEW_UNPUBLISH_ASSET,
    trackUnpublish(reviewUnpublishAssetSaga, (action) =>
      action.statusType === UNPUBLISHED ? unpublishTarget(action) : null,
    ),
  )
}

export function* reviewUnpublishAssetSaga(action) {
  const {
    assets, // list of asset objects
    parent, // folder id string,
    statusType,
    comments,
    type,
  } = action

  yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })

  try {
    const config = yield select((state) => state.config.appConfig)
    const allAssets = yield select((state) => state.assets.byId)
    const data = yield call(appActions.refreshStateSaga)
    const allFolders = yield select((state) => state.folders.byId)

    if (helpers.validateReviewUnpublishRequest(action, data)) {
      const timestamp = yield call(Date.now)
      const user = yield select((state) => state.user)
      action.user = user.id
      action.client = user.client
      action.timestamp = timestamp

      if (statusType === UNPUBLISHED) {
        yield call(retainInUseAssetsSaga, {
          config,
          user,
          data,
          assets,
          folders: [parent],
        })
      }

      // ** CREATE events for the events table ** //
      let events = []
      for (let asset of assets) {
        events.push({
          target_id: asset,
          event_type: type,
          timestamp,
          user: user.id,
          attributes: [
            {
              name: 'status',
              old_value: APPROVED,
              new_value: statusType,
            },
            {
              name: 'folder',
              old_value: parent,
              new_value: parent,
            },
            {
              name: 'reviewer',
              old_value: null,
              new_value: user.id,
            },
            {
              name: 'comments',
              old_value: null,
              new_value: comments,
            },
          ],
        })
      }
      // ** END CREATE  ** //

      // ** ADD Actions/Events to DAM ** //
      yield call(apiActions.secureFetchSaga, {
        url: `${config.baseUrl}/api/state/addaction`,
        init: {
          method: 'POST',
          body: JSON.stringify({ action, events }),
        },
      })
      // ** END ADD ** //

      // ** CALL to force update the DAM state ** //
      yield call(apiActions.secureFetchSaga, {
        url: `${config.baseUrl}/api/state/sns/update`,
        init: {
          method: 'GET',
        },
      })
      // ** END CALL ** //

      // ** UPDATE the local Redux store ** //
      yield put({
        type: REVIEW_UNPUBLISH_ASSET_SUCCESS,
        assets: assets,
        parent: parent,
        comments: comments,
        statusType: statusType,
        user: user.id,
        timestamp,
      })
      // ** End UPDATE ** //

      yield put({ type: appActions.COMPLETE_ASYNC })

      // ** DISPLAY UI notification messages ** //
      let msg
      if (assets.length > 1) {
        if (statusType === UNPUBLISHED) {
          msg = `${assets.length} assets have been removed from ${allFolders[parent].name}`
        } else {
          msg = `${assets.length} are still published within ${allFolders[parent].name}`
        }
        message.success(msg, 4)
      } else {
        if (statusType === UNPUBLISHED) {
          msg = (
            <span>
              {assetName(allAssets[assets[0]])} has been removed from{' '}
              {allFolders[parent].name}
            </span>
          )
        } else {
          msg = (
            <span>
              {assetName(allAssets[assets[0]])} is still published within{' '}
              {allFolders[parent].name}
            </span>
          )
        }
        message.success(msg, 4)
      }
      // ** END DISPLAY ** //

      yield put({ type: appActions.CLEAR_SELECTED_ASSETS })

      // ** LOG Analytics ** //
      for (let asset of assets) {
        yield fork(
          recordAnalyticsEvent,
          statusType === UNPUBLISHED
            ? analyticsConstants.UNPUBLISH_ASSET_APPROVE
            : analyticsConstants.UNPUBLISH_ASSET_REJECT,
          { asset: data.assetState.byId[asset] },
        )
      }
      // ** END LOG Analytics ** //
    } else {
      // validateReviewUnpublishRequest has returned FALSEY
      yield put({ type: appActions.COMPLETE_ASYNC })
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: {
          type: 'ERROR',
          msg:
            'Asset is not PENDING_REMOVAL.  The application is refreshed...please check ' +
            'status and try again',
        },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
      yield put({ type: appActions.CLEAR_SELECTED_ASSETS })
    }
  } catch (error) {
    console.log(error)
    // Session Timeout and user chooses to QUIT
    if (error instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    } else {
      // Something else has thrown inside the saga
      yield put({ type: appActions.COMPLETE_ASYNC })
      yield put({
        type: modalActions.SET_MESSAGE,
        msg: {
          type: 'ERROR',
          msg: error.clientMessage || 'Asset review failed',
        },
      })
      yield put({ type: modalActions.OPEN_MODAL, modal: 'MessageModal' })
    }
  }
}

export function* watchReviewAssetSuccess() {
  yield takeEvery(
    [REVIEW_ASSET_SUCCESS, REVIEW_UNPUBLISH_ASSET_SUCCESS],
    notificationActions.sendReviewNotificationSaga,
  )
}

export const loadEndpointState = (endpoints, home) => ({
  type: LOAD_ENDPOINT_STATE,
  payload: endpoints,
  home,
})

export default all([
  watchAddEndpoint(),
  watchDeleteEndpoint(),
  watchPublishAsset(),
  watchPublishAssetSuccess(),
  watchPublishAndApproveAsset(),
  watchRenameEndpoint(),
  watchReviewAsset(),
  watchReviewAssetSuccess(),
  watchUnpublishAsset(),
  watchUnpublishAndApprove(),
  watchMovePublishedAssets(),
  watchUnpublishAndDelete(),
  watchReviewUnpublishAsset(),
  watchUnpublish(),
  watchBatchPublish(),
  watchPublishFolder(),
  watchPublishAndApproveFolder(),
  watchUnpublishFolder(),
])
